import type { SaveFile } from '../model/types';
import { FUNCTION_ERROR_MESSAGES } from '../server/functionErrors';

export interface AuthUser {
  id: string;
  isAnonymous: boolean;
  email: string | null;
}

export interface RemoteApi {
  currentUser(): Promise<AuthUser | null>;
  signInAnonymously(): Promise<AuthUser>;
  linkGoogle(redirectTo: string): Promise<void>;
  signInWithGoogle(redirectTo: string): Promise<void>;
  signOut(): Promise<void>;
  rpc<T>(name: string, args: Record<string, unknown>): Promise<T>;
  invoke<T>(name: string, body: Record<string, unknown>, options?: { keepalive?: boolean }): Promise<T>;
  readSave(): Promise<SaveFile | null>;
  insertFeedback?(row: FeedbackInsert): Promise<void>;
  uploadFeedbackShot?(path: string, bytes: Uint8Array): Promise<void>;
  attachFeedbackScreenshot?(reportId: string): Promise<void>;
}

export interface FeedbackInsert {
  id: string;
  kind: 'bug' | 'idea';
  message: string;
  game_version: string;
  user_agent: string;
  current_stage: number;
  recent_errors: string[];
  save_version: number;
  url: string;
}

export type RemoteErrorCode =
  | 'save_conflict'
  | 'superseded'
  | 'not_authenticated'
  | 'save_invalid'
  | 'too_fast'
  | 'batch_stale'
  | 'batch_gap'
  | 'batch_stage'
  | 'network'
  | 'unknown';

export class RemoteError extends Error {
  constructor(
    readonly code: RemoteErrorCode,
    message?: string,
    readonly retryAfterMs?: number,
  ) {
    super(message ?? code);
  }
}

export function remoteErrorFromMessage(message: string): RemoteError {
  if (message.includes('too_fast')) return new RemoteError('too_fast');
  if (message.includes('batch_stale')) return new RemoteError('batch_stale', FUNCTION_ERROR_MESSAGES.batch_stale);
  if (message.includes('batch_gap')) return new RemoteError('batch_gap', FUNCTION_ERROR_MESSAGES.batch_gap);
  if (message.includes('batch_stage')) return new RemoteError('batch_stage', FUNCTION_ERROR_MESSAGES.batch_stage);
  if (message.includes('save_conflict')) return new RemoteError('save_conflict');
  if (message.includes('superseded')) return new RemoteError('superseded');
  if (message.includes('not_authenticated')) return new RemoteError('not_authenticated');
  if (message.includes('save_invalid') || message.includes('missing_save')) return new RemoteError('save_invalid');
  if (message.includes('Failed to fetch') || message.includes('NetworkError') || message.includes('network')) {
    return new RemoteError('network');
  }
  return new RemoteError('unknown', message);
}
